import React, { useState, useRef, useEffect } from 'react';
import { BookmarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import axios from 'axios';

const HighlightableText = ({ content, userId, nodeId, categoryId }) => {
  const [selectedText, setSelectedText] = useState('');
  const [showSaveButton, setShowSaveButton] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [selectionPosition, setSelectionPosition] = useState({ top: 0, left: 0 });
  const contentRef = useRef(null);
  const saveButtonRef = useRef(null);

  useEffect(() => {
    const handleSelection = () => {
      const selection = window.getSelection();
      const text = selection.toString().trim();

      if (text.length > 0) {
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        const contentRect = contentRef.current?.getBoundingClientRect();

        if (contentRect) {
          setSelectedText(text);
          setShowSaveButton(true);
          setSaved(false);
          setSelectionPosition({
            top: rect.bottom - contentRect.top + 10,
            left: rect.left - contentRect.left + rect.width / 2,
          });
        }
      } else {
        setShowSaveButton(false);
      }
    };

    const handleClickOutside = (e) => {
      if (saveButtonRef.current && !saveButtonRef.current.contains(e.target)) {
        const selection = window.getSelection();
        if (selection.toString().trim().length === 0) setShowSaveButton(false);
      }
    };

    document.addEventListener('selectionchange', handleSelection);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('selectionchange', handleSelection);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSaveToNotes = async () => {
    if (!selectedText || !userId) return;

    setSaving(true);
    try {
      await axios.post('/api/notes/', {
        title: `Learning Note: ${nodeId}`,
        content: selectedText,
        tags: ['learning', categoryId, nodeId],
        linked_type: 'learning_lesson',
        linked_id: `${categoryId}/${nodeId}`,
      });

      setSaved(true);
      setShowSaveButton(false);
      window.getSelection().removeAllRanges();
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      alert('Failed to save to notes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div ref={contentRef} className="relative select-text prose-editorial">
      <div dangerouslySetInnerHTML={{ __html: content }} />

      {showSaveButton && selectedText && (
        <div
          ref={saveButtonRef}
          className="absolute z-50 flex items-center gap-2 surface-panel px-3 py-2 border border-hairline"
          style={{
            top: `${selectionPosition.top}px`,
            left: `${selectionPosition.left}px`,
            transform: 'translateX(-50%)',
          }}
        >
          {saved ? (
            <>
              <CheckIcon className="w-4 h-4 text-pine" />
              <span className="text-label text-primary">Saved to notes</span>
            </>
          ) : (
            <>
              <BookmarkIcon className="w-4 h-4 text-pine" />
              <button
                type="button"
                onClick={handleSaveToNotes}
                disabled={saving}
                className="text-label text-pine hover:underline disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save to notes'}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default HighlightableText;
